/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { colors, shadows, transitions, borderRadius } from '../../styles/theme';
import { NavLink } from '../../types';

const navLinks: NavLink[] = [
  { label: 'Ana Sayfa', path: '/' },
  { label: 'Blog', path: '/blog' },
  { label: 'Kategoriler', path: '/kategoriler' },
  { label: 'Nasıl Kullanılır?', path: '/nasil-kullanilir' },
  { label: 'Hakkımızda', path: '/hakkimizda' },
];

// Tam menü + butonlar bu genişlikten itibaren tek satıra sığıyor; altında hamburger menü
const desktop = '@media (min-width: 1200px)';
const HEADER_HEIGHT = 70;

function isLinkActive(linkPath: string, pathname: string) {
  if (linkPath === '/') return pathname === '/';
  if (linkPath === '/blog') return pathname === '/blog' || pathname.startsWith('/blog/');
  if (linkPath === '/kategoriler') return pathname === '/kategoriler' || pathname.startsWith('/kategori/');
  return pathname === linkPath || pathname.startsWith(`${linkPath}/`);
}

// Marka rengi (logo zemini turkuaz) header zemininde kullanılıyor
const headerStyles = css`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${colors.primary[500]};
  box-shadow: none;
  border-bottom: 1px solid transparent;
  transition: all 0.3s ease;

  &.scrolled,
  &.menu-open {
    background: ${colors.primary[600]};
    box-shadow: ${shadows.md};
    border-bottom-color: ${colors.primary[700]};
  }
`;

const containerStyles = css`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  height: ${HEADER_HEIGHT}px;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const logoStyles = css`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-shrink: 0;
  text-decoration: none;
  color: white;
  font-weight: 800;
  font-size: 1.375rem;

  &:hover {
    color: white;
  }
`;

const logoIconStyles = css`
  width: 40px;
  height: 40px;
  border-radius: ${borderRadius.lg};
  object-fit: contain;
`;

const navStyles = css`
  display: none;
  align-items: center;
  gap: 0.25rem;

  ${desktop} {
    display: flex;
  }
`;

const navLinkStyles = css`
  padding: 0.5rem 0.75rem;
  color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.925rem;
  white-space: nowrap;
  border-radius: ${borderRadius.lg};
  transition: all ${transitions.fast};

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.15);
  }
`;

const navLinkActiveStyles = css`
  color: white;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
`;

const actionsStyles = css`
  display: none;
  align-items: center;
  gap: 0.625rem;

  ${desktop} {
    display: flex;
  }
`;

const actionButtonStyles = css`
  display: inline-flex;
  align-items: center;
  padding: 0.625rem 1.1rem;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
  border-radius: ${borderRadius.lg};
  text-decoration: none;
  transition: all ${transitions.fast};

  &:hover {
    color: white;
    transform: translateY(-2px);
  }
`;

const driverButtonStyles = css`
  background: linear-gradient(135deg, ${colors.secondary[500]}, ${colors.secondary[600]});
  box-shadow: 0 4px 12px ${colors.secondary[500]}30;

  &:hover {
    box-shadow: 0 6px 16px ${colors.secondary[500]}40;
  }
`;

const helpButtonStyles = css`
  background: white;
  color: ${colors.primary[700]};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

  &:hover {
    color: ${colors.primary[700]};
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.18);
  }
`;

// Beyaz çekmecede "Hemen Yardım Al" butonu marka renginde görünür
const drawerHelpButtonStyles = css`
  background: linear-gradient(135deg, ${colors.primary[600]}, ${colors.primary[700]});
  color: white;

  &:hover {
    color: white;
  }
`;

const hamburgerStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 44px;
  height: 44px;
  margin-right: -0.5rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: ${borderRadius.lg};
  cursor: pointer;

  ${desktop} {
    display: none;
  }

  span {
    display: block;
    width: 22px;
    height: 2px;
    background: white;
    border-radius: 2px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:nth-of-type(1) {
      margin-bottom: 6px;
    }

    &:nth-of-type(2) {
      margin-bottom: 6px;
    }
  }

  &.open span:nth-of-type(1) {
    transform: translateY(8px) rotate(45deg);
  }

  &.open span:nth-of-type(2) {
    opacity: 0;
    transform: translateX(-10px);
  }

  &.open span:nth-of-type(3) {
    transform: translateY(-8px) rotate(-45deg);
  }
`;

// Çekmece header'ın altından açılır; böylece kapatma butonu her zaman görünür kalır
const backdropStyles = css`
  position: fixed;
  top: ${HEADER_HEIGHT}px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 98;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;

  &.open {
    opacity: 1;
    pointer-events: auto;
  }

  ${desktop} {
    display: none;
  }
`;

const drawerStyles = css`
  position: fixed;
  top: ${HEADER_HEIGHT}px;
  right: 0;
  bottom: 0;
  width: 300px;
  max-width: 85vw;
  background: white;
  z-index: 99;
  padding: 1.25rem 1rem 2rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  box-shadow: ${shadows['2xl']};
  transform: translateX(100%);
  visibility: hidden;
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.35s;

  &.open {
    transform: translateX(0);
    visibility: visible;
  }

  ${desktop} {
    display: none;
  }
`;

const drawerLinkStyles = css`
  padding: 0.85rem 1rem;
  color: ${colors.text.secondary};
  text-decoration: none;
  font-weight: 500;
  font-size: 1.05rem;
  border-radius: ${borderRadius.lg};
  transition: all ${transitions.fast};

  &:hover {
    color: ${colors.primary[700]};
    background: ${colors.primary[50]};
  }
`;

const drawerLinkActiveStyles = css`
  color: ${colors.primary[700]};
  background: ${colors.primary[50]};
  font-weight: 600;
`;

const drawerDividerStyles = css`
  height: 1px;
  background: ${colors.neutral[200]};
  margin: 0.75rem 0;
`;

const drawerCtaStyles = css`
  justify-content: center;
  padding: 0.85rem 1.25rem;
  font-size: 0.95rem;
  margin-top: 0.5rem;

  &:hover {
    transform: none;
  }
`;

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const headerClassName = [scrolled && 'scrolled', mobileMenuOpen && 'menu-open']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <header css={headerStyles} className={headerClassName}>
        <div css={containerStyles}>
          <Link to="/" css={logoStyles}>
            <img css={logoIconStyles} src="/goyazililogo.webp" alt="" width={40} height={40} />
            <span>YolSepetiGO</span>
          </Link>

          <nav css={navStyles} aria-label="Ana menü">
            {navLinks.map((link) => {
              const active = isLinkActive(link.path, location.pathname);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  css={[navLinkStyles, active && navLinkActiveStyles]}
                  aria-current={active ? 'page' : undefined}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div css={actionsStyles}>
            <a
              href="https://wa.me/905550103434"
              target="_blank"
              rel="noopener noreferrer"
              css={[actionButtonStyles, driverButtonStyles]}
            >
              Sürücü Olmak İstiyorum
            </a>
            <a
              href="https://yolsepetigo.com"
              target="_blank"
              rel="noopener noreferrer"
              css={[actionButtonStyles, helpButtonStyles]}
            >
              Hemen Yardım Al
            </a>
          </div>

          <button
            css={hamburgerStyles}
            className={mobileMenuOpen ? 'open' : ''}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? 'Menüyü kapat' : 'Menüyü aç'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </header>

      <div
        css={backdropStyles}
        className={mobileMenuOpen ? 'open' : ''}
        onClick={() => setMobileMenuOpen(false)}
      />

      <nav
        id="mobile-menu"
        css={drawerStyles}
        className={mobileMenuOpen ? 'open' : ''}
        aria-label="Mobil menü"
      >
        {navLinks.map((link) => {
          const active = isLinkActive(link.path, location.pathname);
          return (
            <Link
              key={link.path}
              to={link.path}
              css={[drawerLinkStyles, active && drawerLinkActiveStyles]}
              aria-current={active ? 'page' : undefined}
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          );
        })}
        <div css={drawerDividerStyles} />
        <a
          href="https://wa.me/905550103434"
          target="_blank"
          rel="noopener noreferrer"
          css={[actionButtonStyles, driverButtonStyles, drawerCtaStyles]}
        >
          Sürücü Olmak İstiyorum
        </a>
        <a
          href="https://yolsepetigo.com"
          target="_blank"
          rel="noopener noreferrer"
          css={[actionButtonStyles, helpButtonStyles, drawerHelpButtonStyles, drawerCtaStyles]}
        >
          Hemen Yardım Al
        </a>
      </nav>
    </>
  );
}
